import React from "react";
import { useTranslation } from "react-i18next";
import {
  ScanLine, Keyboard, Search, CornerDownLeft, ChevronRight, Store, MapPin, Package, AlertTriangle,
} from "lucide-react";
import { cn } from "../../../utils/cn";
import { Badge } from "../../../components/ui/Badge";
import SearchableSelect from "../../../components/ui/SearchableSelect";
import { packageStatus } from "../../../utils/packageStatus";
import { OUTCOME_META, FLASH_WRAP, FLASH_TEXT, FLASH_ICON_BG } from "./outcome";
import { recipientName, comercioName, comunaName, SAMPLE_CODES } from "./mockData";

function ModeButton({ active, onClick, icon: Icon, children }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium transition-colors",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        active ? "bg-brand-500 text-white shadow-sm" : "text-fg-muted hover:bg-bg hover:text-fg",
      )}
    >
      <Icon className="w-4 h-4" />
      {children}
    </button>
  );
}

function FlashResult({ lastResult, lastOrder, onOpenDetail }) {
  const { t } = useTranslation();

  if (!lastResult) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-bg-muted/40 px-6 py-8 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-bg text-fg-subtle">
          <ScanLine className="h-6 w-6" />
        </div>
        <p className="text-sm font-medium text-fg">{t("Listo para recibir")}</p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-fg-muted">
          {t("Escanea el primer código con la pistola o escríbelo a mano para registrarlo.")}
        </p>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5">
          <span className="text-[11px] uppercase tracking-wider text-fg-subtle">{t("Prueba con")}:</span>
          {SAMPLE_CODES.map((c) => (
            <span key={c} className="rounded-md bg-bg px-2 py-0.5 font-mono text-xs text-fg-muted border border-border">{c}</span>
          ))}
        </div>
      </div>
    );
  }

  const meta = OUTCOME_META[lastResult.outcome] || OUTCOME_META.notfound;
  const { tone, Icon } = meta;

  return (
    <div
      // key fuerza el re-montaje -> re-dispara la animación en cada escaneo
      key={lastResult.ts}
      aria-live="polite"
      className={cn(
        "rounded-xl border-2 p-5 animate-scale-in motion-reduce:animate-none",
        FLASH_WRAP[tone],
      )}
    >
      <div className="flex items-start gap-4">
        <div className={cn("flex h-12 w-12 shrink-0 items-center justify-center rounded-xl", FLASH_ICON_BG[tone])}>
          <Icon className="h-7 w-7" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={cn("text-lg font-bold", FLASH_TEXT[tone])}>{t(meta.label)}</span>
            <span className="font-mono text-lg font-semibold text-fg tabular-nums">#{lastResult.code}</span>
            {lastResult.mismatch && (
              <Badge tone="warning" className="gap-1">
                <AlertTriangle className="h-3.5 w-3.5" />{t("Otro comercio")}
              </Badge>
            )}
          </div>

          {lastOrder ? (
            <div className="mt-1.5 space-y-1 text-sm text-fg">
              <div className="flex items-center gap-1.5">
                <Package className="h-4 w-4 text-fg-muted shrink-0" />
                <span className="font-medium truncate">{recipientName(lastOrder)}</span>
                {lastResult.outcome === "already" && (
                  <Badge tone="info" className="ml-1">{t("Estado")}: {t(packageStatus[lastOrder.status] || "—")}</Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-fg-muted">
                <span className="inline-flex items-center gap-1"><Store className="h-3.5 w-3.5" />{comercioName(lastOrder)}</span>
                <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{comunaName(lastOrder)}</span>
              </div>
            </div>
          ) : (
            <p className="mt-1.5 text-sm text-fg-muted">
              {t("No encontramos ese código. Verifica el número o búscalo manualmente.")}
            </p>
          )}
        </div>

        {lastOrder && (
          <button
            type="button"
            onClick={() => onOpenDetail(lastOrder)}
            className="shrink-0 inline-flex min-h-[40px] items-center gap-1 self-center rounded-lg px-2.5 py-1.5 text-sm font-medium text-fg-muted hover:bg-bg/60 hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            {t("Ver detalle")}<ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function ScanConsole({
  mode, onModeChange, query, onQueryChange, onSubmit,
  lastResult, lastOrder, onOpenDetail, listening,
  manualMatches = [], onPickManual, inputRef,
  clients = [], clientId, onClientChange,
}) {
  const { t } = useTranslation();
  const isManual = mode === "manual";

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSubmit(query);
    }
  };

  return (
    <div className="bg-bg border border-border rounded-xl shadow-card p-5 space-y-4">
      {/* cabecera: cliente asignado + selector de modo. En mobile se apila
          (label arriba, select ancho completo, toggle debajo) para que el
          select no pise el toggle de modo. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-col gap-1.5 sm:flex-1 sm:flex-row sm:items-center sm:gap-2">
          <span className="text-sm font-medium text-fg-muted shrink-0">{t("Recibiendo para")}</span>
          <div className="w-full sm:max-w-xs">
            <SearchableSelect
              options={clients.map((c) => ({ value: c.id, label: c.name }))}
              value={clientId}
              onChange={onClientChange}
              placeholder={t("Selecciona el cliente")}
              isClearable
            />
          </div>
        </div>
        <div className="inline-flex items-center gap-1 self-start rounded-xl bg-bg-muted p-1 sm:self-auto">
          <ModeButton active={!isManual} onClick={() => onModeChange("scanner")} icon={ScanLine}>{t("Pistola")}</ModeButton>
          <ModeButton active={isManual} onClick={() => onModeChange("manual")} icon={Keyboard}>{t("Manual")}</ModeButton>
        </div>
      </div>

      {/* estado de escucha */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {!isManual && listening ? (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-status-success">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-status-success opacity-60 animate-ping motion-reduce:hidden" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-status-success" />
              </span>
              {t("Pistola lista — escuchando")}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-fg-muted">
              <Keyboard className="h-4 w-4" />
              {isManual ? t("Ingreso manual") : t("Pausado")}
            </span>
          )}
        </div>
      </div>

      {/* input grande */}
      <div className="relative">
        <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-fg-subtle">
          {isManual ? <Search className="h-6 w-6" /> : <ScanLine className="h-6 w-6" />}
        </div>
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={handleKeyDown}
          // En modo pistola el input recibe el foco solo para capturar el HID del
          // lector: evitamos el teclado virtual (inputMode="none"); en manual sí.
          inputMode={isManual ? "text" : "none"}
          autoComplete="off"
          spellCheck={false}
          aria-label={t("Número de seguimiento del pedido")}
          placeholder={isManual ? t("Escribe el N° de seguimiento o el destinatario…") : t("Escanea o escribe el N° de seguimiento…")}
          className={cn(
            "w-full rounded-xl border-2 border-border bg-bg pl-14 pr-32 text-fg font-mono",
            "h-16 text-2xl tabular-nums tracking-wide",
            "placeholder:font-sans placeholder:text-base placeholder:tracking-normal placeholder:text-fg-subtle",
            "focus:outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15 transition-colors",
          )}
        />
        <button
          type="button"
          onClick={() => onSubmit(query)}
          disabled={!query.trim()}
          className={cn(
            "absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 h-12 px-4 rounded-lg",
            "bg-brand-500 text-white font-medium hover:bg-brand-600 active:bg-brand-700 transition-colors",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
            "disabled:opacity-40 disabled:cursor-not-allowed",
          )}
        >
          {t("Recibir")}
          <CornerDownLeft className="h-4 w-4" />
        </button>
      </div>

      {/* sugerencias en modo manual */}
      {isManual && query.trim() && (
        <div className="rounded-xl border border-border overflow-hidden divide-y divide-border">
          {manualMatches.length === 0 ? (
            <div className="px-4 py-3 text-sm text-fg-muted">{t("Sin pedidos pendientes que coincidan.")}</div>
          ) : (
            manualMatches.slice(0, 6).map((o) => (
              <button
                key={o._id}
                type="button"
                onClick={() => onPickManual(o)}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-bg-muted focus:outline-none focus-visible:bg-bg-muted"
              >
                <span className="font-mono text-sm font-semibold text-fg tabular-nums w-20 shrink-0">{o.followingNumber}</span>
                <span className="min-w-0 flex-1 truncate text-sm text-fg">{recipientName(o)}</span>
                <span className="hidden sm:inline truncate text-sm text-fg-muted max-w-[40%]">{comercioName(o)}</span>
                <ChevronRight className="h-4 w-4 text-fg-subtle shrink-0" />
              </button>
            ))
          )}
        </div>
      )}

      {/* resultado del último escaneo */}
      <FlashResult lastResult={lastResult} lastOrder={lastOrder} onOpenDetail={onOpenDetail} />
    </div>
  );
}
